/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MRT_ColumnDef } from 'material-react-table';

import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import { getStoredCustomerDetails, itemsPerPage } from '../../../../../Shared/Utilities/utils';
import { IJobOverview } from '../../../../Jobs/Components/JobsListView/JobsListView';
import { TaskData } from '../../../../Jobs/Components/WorkTasks/WorkTasks';
import { IJobs } from '../../../../Jobs/Models/JobsView.Model';
import { JobManagementType } from '../../AllJobs/AllJobs.Model';
import { IBidList } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import { CurrencySymbol } from '../../../../../Shared/Constants/App';
import DataGrid from '../../../../../Shared/Components/Common/DataGrid/DataGrid';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import ProofSettingOptionsPopup from './ProofSettingOptionsPopup';

import { ModifyButtonContainer } from '../AllJobs.Style';
import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { StyledPagination } from '../../../../../Shared/Components/Common/CommonStyle/Pagination.Style';

const ModifyProofSettings = (): JSX.Element => {
  const location = useLocation();
  const jobsData = location?.state?.selectedRows;
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isSchedulesLoading, setIsSchedulesLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const { t } = useTranslation(['jobManagement']);
  const storedWoScheduleMasterIds = localStorage.getItem('woScheduleMasterIds');
  const woScheduleMasterIds = storedWoScheduleMasterIds ? JSON.parse(storedWoScheduleMasterIds) : null;
  const { userId } = getStoredCustomerDetails() || {};

  const fetchAllSchedules = async (currentPage: number, limit: number, woScheduleMasterIds: string[] | null) => {
    setIsSchedulesLoading(true);
    try {
      const response = await AllJobsApiService.allSchedules(currentPage, limit, userId, {
        woScheduleMasterId: woScheduleMasterIds || undefined,
      });
      if (response.data.data) {
        const filteredData = woScheduleMasterIds
          ? response.data.data.filter((schedule: any) => woScheduleMasterIds.includes(schedule.woScheduleMasterId))
          : response.data.data;
        setTableData(filteredData);
      }
      setTotalCount(response?.data?.metadata?.totalCount);
    } catch (error) {
      console.error(t('messages:errorFetchingData'), error);
    } finally {
      setIsSchedulesLoading(false);
    }
  };

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);
  };

  const handlePaginationChange = (event: any, value: number) => {
    handleChange(event, value);
    setCurrentPage(value);
    void fetchAllSchedules(value, itemsPerPage, woScheduleMasterIds);
  };

  const renderCell = useCallback((renderedCellValue: React.ReactNode): string | number => {
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    if (renderedCellValue && Array.isArray(renderedCellValue)) {
      return renderedCellValue[0]?.finalQuote || renderedCellValue[0]?.finalQuote === 0
        ? `${CurrencySymbol.Dollar}${Number(renderedCellValue[0]?.finalQuote).toFixed(2)}`
        : '-';
    }
    return '-';
  }, []);

  const columns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    () => [
      {
        accessorKey: 'jobNumber',
        header: 'Job Number',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobName',
        header: 'Job Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobAddress',
        header: 'Job Address',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'scheduleName',
        header: 'Schedule Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'vendorName',
        header: 'Vendor Name',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
    ],
    []
  );

  useEffect(() => {
    if (jobsData && jobsData.length > 0) {
      const woScheduleMasterIds = jobsData.map((job: any) => job?.woScheduleMasterId).filter(Boolean);
      localStorage.setItem('woScheduleMasterIds', JSON.stringify(woScheduleMasterIds));
    }
  }, []);

  useEffect(() => {
    const storedWoScheduleMasterIds = localStorage.getItem('woScheduleMasterIds');
    const woScheduleMasterIds = storedWoScheduleMasterIds ? JSON.parse(storedWoScheduleMasterIds) : null;
    fetchAllSchedules(currentPage, itemsPerPage, woScheduleMasterIds);
  }, [currentPage, itemsPerPage, jobsData]);

  return (
    <>
      <PageTitle title={t('jobManagement:modifyProofSettings')} />
      <CustomerContainer>
        <Box sx={{ marginTop: '1rem' }}>
          <DataGrid
            key={refreshKey}
            columns={columns}
            data={tableData || []}
            enableRowSelect={true}
            enableColumnPinning={false}
            setSelectedRows={setSelectedRows}
            selectedRows={selectedRows}
            configureColumns={false}
            loader={isSchedulesLoading}
            type={JobManagementType.AllSchedules}
          />
        </Box>
        <ModifyButtonContainer sx={{ marginTop: '1rem' }}>
          {selectedRows.length > 0 && (
            <Button
              type={ButtonType.Primary}
              label={t('jobManagement:modifyProofSettings')}
              onClick={() => setIsModalOpen(true)}
            />
          )}
        </ModifyButtonContainer>
        {!isLoading && totalCount > 10 && (
          <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
            <StyledPagination
              count={Math.ceil(totalCount / itemsPerPage)}
              page={currentPage}
              onChange={(event, value) => {
                handlePaginationChange(event, value);
              }}
            />
          </Stack>
        )}
        <ProofSettingOptionsPopup
          key={refreshKey}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedJobs={selectedRows}
          setRefreshKey={setRefreshKey}
        />
      </CustomerContainer>
    </>
  );
};

export default ModifyProofSettings;
